import {
  BadgePercent,
  BookOpen,
  Building2,
  Calendar,
  Clock,
  Coins,
  Inbox,
  LayoutDashboard,
  MessageSquare,
  Receipt,
  Share2,
  UserRound,
  Users,
  Wallet,
  Webhook,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  // The canonical action gating this destination — the item renders only when
  // me.permissions contains it (mirrors the action the backend route
  // authorizes). Undefined = visible to every authenticated user (Dashboard).
  requirePermission?: string;
}

export interface NavSection {
  label: string;
  items: readonly NavItem[];
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    label: 'Operate',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/calendar', label: 'Calendar', icon: Calendar, requirePermission: 'availability.read' },
      { to: '/bookings', label: 'Bookings', icon: BookOpen, requirePermission: 'bookings.read' },
      { to: '/inbox', label: 'Inbox', icon: Inbox, requirePermission: 'threads.read' },
      // For staff with finance.submit but no finance.read (a cleaner or
      // handyman) — the Finance section item below is gated on finance.read
      // and would otherwise be their only door into the section, and they
      // don't have the key. Someone who holds both sees both entries; that's
      // a second, faster way in for them, not a bug.
      { to: '/finance/submit', label: 'Submit expense', icon: Receipt, requirePermission: 'finance.submit' },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { to: '/apartments', label: 'Apartments', icon: Building2, requirePermission: 'apartments.read' },
      { to: '/pricing-strategies', label: 'Pricing', icon: BadgePercent, requirePermission: 'pricing.read' },
      { to: '/guests', label: 'Guests', icon: UserRound, requirePermission: 'guests.read' },
    ],
  },
  {
    label: 'Automation',
    items: [
      { to: '/templates', label: 'Templates', icon: MessageSquare, requirePermission: 'templates.read' },
      { to: '/rules', label: 'Rules', icon: Workflow, requirePermission: 'automation.read' },
      { to: '/automation-scheduled', label: 'Scheduled', icon: Clock, requirePermission: 'automation.read' },
    ],
  },
  {
    label: 'Finance',
    items: [{ to: '/finance', label: 'Finance', icon: Wallet, requirePermission: 'finance.read' }],
  },
  {
    label: 'Settings',
    items: [
      // Channel management is a write surface (connect/disconnect mutates
      // apartment↔OTA state) — read-only roles don't get the page.
      { to: '/settings/channels', label: 'Channels', icon: Share2, requirePermission: 'apartments.write' },
      { to: '/users', label: 'Users', icon: Users, requirePermission: 'users.read' },
      { to: '/webhooks', label: 'Webhooks', icon: Webhook, requirePermission: 'webhooks.read' },
      // Categories, counterparties, channel routing and FX are the finance
      // block's reference data — finance.manage, not finance.read.
      { to: '/settings/finance', label: 'Finance settings', icon: Coins, requirePermission: 'finance.manage' },
    ],
  },
];

// hasNavAccess reports whether a permission set may see the nav item.
// Accepts a minimal shape so tests don't need to build full NavItems.
export function hasNavAccess(
  permissions: string[],
  item: Pick<NavItem, 'to' | 'label' | 'requirePermission'>,
): boolean {
  if (!item.requirePermission) return true;
  return permissions.includes(item.requirePermission);
}

// Active-route logic: exact for '/'; otherwise prefix (so '/bookings' lights up
// on '/bookings' and '/bookings/$id' alike).
export function isNavActive(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname.startsWith(to);
}

// Humanise the user's primary role for the sidebar footer.
export function formatRole(role?: string): string {
  if (!role) return 'Team member';
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
}

// The page title shown in the mobile topbar — the label of the active nav item.
export function navTitle(pathname: string): string {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (isNavActive(item.to, pathname)) return item.label;
    }
  }
  return 'Ollio Ops';
}

// Static descriptive subtitle per top-level route — the Topbar fallback shown
// when a page hasn't published a dynamic (count-bearing) subtitle via
// usePageHeader. Prefix-matched (navSubtitle below), so detail routes and
// sub-tabs inherit their section's text unless they have their own, more
// specific entry — see navSubtitle for how a more specific entry wins.
const NAV_SUBTITLE: Record<string, string> = {
  '/': "Today's overview",
  '/calendar': 'Availability, rates & restrictions',
  '/bookings': 'All bookings across every channel',
  '/inbox': 'Guest messaging across every channel',
  '/apartments': 'Units, photos & channel mapping',
  '/pricing-strategies': 'Reusable rate strategies & rules',
  '/guests': 'Profiles, history & merge',
  '/templates': 'Message templates & variables',
  '/rules': 'Automation triggers & actions',
  '/automation-scheduled': 'Queued & sent automation',
  '/settings/channels': 'OTA & direct channel connections',
  '/settings/finance': 'Categories, counterparties, channel defaults, FX rates',
  '/finance': 'Money in and out, by account',
  // These three are prefixed by '/finance' above — navSubtitle picks the
  // LONGEST matching key, not the first declared, so a more specific tab
  // always wins over the section's generic text regardless of where either
  // line sits in this object (pinned by a test).
  '/finance/queue': 'Payments awaiting confirmation',
  '/finance/events': 'Match provider payments and investigate refunds',
  '/finance/periods': 'Close the month',
  '/finance/rules': 'Recurring, per-stay and commission rules',
  '/finance/submit': 'Send an expense from your phone',
  '/users': 'Team, roles & invitations',
  '/webhooks': 'Outgoing event subscriptions',
};

export function navSubtitle(pathname: string): string {
  if (pathname === '/') return NAV_SUBTITLE['/'] ?? '';
  // Longest-prefix-wins: '/finance/queue' must beat '/finance' for a queue
  // path regardless of which key was declared first in NAV_SUBTITLE above.
  let best: string | undefined;
  for (const k of Object.keys(NAV_SUBTITLE)) {
    if (k === '/' || !pathname.startsWith(k)) continue;
    if (!best || k.length > best.length) best = k;
  }
  return best ? (NAV_SUBTITLE[best] ?? '') : '';
}

// Flat list of navigable destinations for the ⌘K command palette.
export interface NavCommand {
  to: string;
  label: string;
  icon: LucideIcon;
}
export function navCommands(permissions: string[]): NavCommand[] {
  return NAV_SECTIONS.flatMap((s) =>
    s.items
      .filter((i) => hasNavAccess(permissions, i))
      .map((i) => ({ to: i.to, label: i.label, icon: i.icon })),
  );
}
